/**
 * DeployLogViewer — sdílená komponenta pro zobrazení deploy logů.
 *
 * Načítá poslední N runs přes MCP `toolbox.deploy_logs` { module, instance },
 * ukazuje je v modalu s tab listem per run a monospace terminálovým view.
 *
 * Použití:
 *   <DeployLogViewer
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     module="apphost"          // nebo "supabase"
 *     instance={inst.name}
 *   />
 *
 * Backend: acm.deploy_logs tabulka, zapisuje src/modules/deploy-log.ts (startDeployLog).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './Modal';
import { Badge } from './Badge';
import { AlertCircle, CheckCircle2, Clock, Loader2, RefreshCw } from 'lucide-react';
import { api } from '../../lib/api';

interface DeployLogLine {
  ts: string;
  phase: string | null;
  level: 'info' | 'warn' | 'error' | 'cmd';
  text: string;
}

interface DeployRun {
  id: string;
  started_at: string;
  finished_at: string | null;
  status: 'running' | 'success' | 'error';
  phase: string | null;
  lines: DeployLogLine[];
  error: string | null;
  exit_code: number | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  module: string;
  instance: string;
}

export function DeployLogViewer({ open, onClose, module, instance }: Props) {
  const { t, i18n } = useTranslation();
  const [runs, setRuns] = useState<DeployRun[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.mcp<{ ok: boolean; runs: DeployRun[] }>(
        'toolbox.deploy_logs',
        { module, instance, limit: 15 },
      );
      setRuns(resp.runs ?? []);
      if (!selected && resp.runs?.length) setSelected(resp.runs[0].id);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, module, instance]);

  // Auto-refresh pokud aktuální run ještě běží
  useEffect(() => {
    if (!open) return;
    const sel = runs.find((r) => r.id === selected);
    if (!sel || sel.status !== 'running') return;
    const iv = setInterval(() => load(), 2_500);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selected, runs]);

  const current = useMemo(() => runs.find((r) => r.id === selected) ?? null, [runs, selected]);

  return (
    <Modal open={open} onClose={onClose} title={t('ui.deployLogTitle', 'Deploy log — {{instance}}', { instance })} size='xl'>
      {error && (
        <div className='bg-red-900/30 border border-red-500/30 text-red-300 text-xs p-2 rounded mb-3'>
          {error}
        </div>
      )}

      <div className='flex gap-3 h-[60vh]'>
        {/* Left: runs list */}
        <div className='w-64 shrink-0 flex flex-col'>
          <div className='flex items-center justify-between mb-2'>
            <span className='text-xs text-gray-400 font-medium'>{t('ui.deployLogRecent', 'Poslední spuštění')}</span>
            <button
              type='button'
              onClick={load}
              disabled={loading}
              className='text-gray-400 hover:text-white disabled:opacity-50'
              title={t('common.refresh', 'Obnovit')}
            >
              {loading ? <Loader2 size={14} className='animate-spin' /> : <RefreshCw size={14} />}
            </button>
          </div>
          <div className='flex-1 overflow-y-auto space-y-1 pr-1'>
            {runs.length === 0 && !loading && (
              <div className='text-xs text-gray-500 italic p-2'>
                {t('ui.deployLogEmpty', 'Žádné deploy logy — spusť deploy a uvidíš je tady.')}
              </div>
            )}
            {runs.map((r) => {
              const isSel = r.id === selected;
              const Icon = r.status === 'success'
                ? CheckCircle2
                : r.status === 'error'
                ? AlertCircle
                : Clock;
              const iconCls = r.status === 'success'
                ? 'text-emerald-400'
                : r.status === 'error'
                ? 'text-red-400'
                : 'text-amber-400';
              const startedDate = new Date(r.started_at);
              const duration = r.finished_at
                ? Math.round((new Date(r.finished_at).getTime() - startedDate.getTime()) / 1000)
                : null;
              return (
                <button
                  key={r.id}
                  type='button'
                  onClick={() => setSelected(r.id)}
                  className={`w-full text-left text-xs p-2 rounded border transition-colors ${
                    isSel
                      ? 'bg-gray-800 border-blue-500/50'
                      : 'bg-gray-900 border-gray-800 hover:border-gray-700'
                  }`}
                >
                  <div className='flex items-center gap-1.5 mb-0.5'>
                    <Icon size={12} className={iconCls} />
                    <span className={`font-medium ${iconCls}`}>{r.status}</span>
                    {r.phase && <Badge variant='gray'>{r.phase}</Badge>}
                  </div>
                  <div className='text-gray-500 font-mono text-[10px]'>
                    {startedDate.toLocaleString(i18n.language === 'cvm' ? 'cs' : i18n.language, {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    {duration !== null && ` · ${duration}s`}
                    {r.exit_code !== null && r.exit_code !== 0 && ` · exit ${r.exit_code}`}
                  </div>
                  {r.error && (
                    <div className='text-red-400 text-[10px] mt-1 line-clamp-2' title={r.error}>
                      {r.error}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: lines of selected run */}
        <div className='flex-1 flex flex-col min-w-0'>
          {!current && (
            <div className='flex-1 flex items-center justify-center text-xs text-gray-500'>
              {t('ui.deployLogPick', 'Vyber spuštění nalevo')}
            </div>
          )}
          {current && (
            <>
              <div className='flex items-center gap-2 mb-2 text-xs'>
                <span className='text-gray-400'>Phases:</span>
                {uniquePhases(current.lines).map((p) => <Badge key={p} variant='gray'>{p}</Badge>)}
                {current.status === 'running' && (
                  <span className='flex items-center gap-1 text-amber-400 ml-auto'>
                    <Loader2 size={12} className='animate-spin' /> {t('common.running', 'běží')}
                  </span>
                )}
              </div>
              <div className='flex-1 acm-terminal p-3 overflow-auto text-[11px] leading-relaxed'>
                {current.lines.length === 0 && (
                  <span className='text-gray-500 italic'>{t('ui.deployLogNoOutput', '(žádný výstup)')}</span>
                )}
                {current.lines.map((line, i) => <LogLine key={i} line={line} />)}
                {current.error && (
                  <div className='mt-2 p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400'>
                    ERROR: {current.error}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

function LogLine({ line }: { line: DeployLogLine }) {
  const cls = line.level === 'error'
    ? 'text-red-400'
    : line.level === 'warn'
    ? 'text-amber-400'
    : line.level === 'cmd'
    ? 'text-blue-400'
    : 'text-gray-300';
  const ts = new Date(line.ts).toLocaleTimeString('cs-CZ', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  return (
    <div className={`whitespace-pre-wrap ${cls}`}>
      <span className='text-gray-600'>[{ts}]</span>{' '}
      {line.phase && <span className='text-gray-500'>[{line.phase}]</span>}{' '}
      {line.level === 'cmd' && '$ '}
      {line.text}
    </div>
  );
}

function uniquePhases(lines: DeployLogLine[]): string[] {
  const set = new Set<string>();
  for (const l of lines) if (l.phase) set.add(l.phase);
  return Array.from(set);
}
